import React, { useState, useTransition, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Tooltip, ClickAwayListener, InputAdornment, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TemporaryUserEdit from '../../components/temporaryUser/temporaryUserEdit/TemporaryUserEdit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { setUserData, setUserMenu } from '../../actions/user';
import { MenuList } from '../../constants/menu';
import QueryString from 'qs';
import { getUsersList } from '../../api/member-management';
import { getTemporaryUsersList } from '../../api/temporaryUser';
import { getHeadBranchs, getHeadBranchId } from '../../api/point';
import {
  StyledAppBar,
  StyledToolbar,
  MenuToggleButton,
  LeftSection,
  TitleWrapper,
  PageTitle,
  BreadcrumbWrapper,
  BreadcrumbText,
  OperatorsText,
  HighlightBorderWrapper,
  SelectGroup,
  SelectWrapper,
  BranchFormControl,
  BranchSelect,
  PlaceholderMenuItem,
  PlaceholderText,
  BranchMenuItem,
  RightSection,
  SearchFieldWrapper,
  StyledAutocomplete,
  SearchTextField,
  SearchPopupIcon,
  RegisterButton,
  UserSection,
  UserName,
  UserAvatar,
  StyledMenu,
  LogoutMenuItem,
  RegisterModal,
  ModalContent,
  BreadcrumbIcon,
  tooltipStyles,
  menuPaperProps,
  menuListProps,
  SidebarGuide,
  SidebarGuideText,
  SidebarGuideClose,
  SidebarGuideArrow,
  CenterSearchInput,
  ChosungDivider,
  SearchOptionRow,
  SearchOptionName,
  SearchOptionPhone,
  SearchTypeBadge,
} from './HeaderStyled';
import { MenuToggleIcon } from '../../components/common/svg/MenuToggleIcon';
import { UserIcon } from '../../components/common/svg/UserIcon';
import { AvatarIcon } from '../../components/common/svg/AvatarIcon';
import UpdateBadge from './UpdateBadge';
import AdminSwitchModal, { ORIGINAL_USER_KEY } from '../../components/adminSwitch/AdminSwitchModal';
import { SwitchButton, RestoreBanner } from '../../components/adminSwitch/AdminSwitchStyled';
import { matchKorean, getChosung } from '../../utils/methods';
import useUserAuth from '../../utils/useUserAuth';
import { clearScheduleClipboard } from '../../components/lesson/scheduleClipboard/scheduleClipboardStore';

// 현재 경로의 상위 경로 반환 (detail 페이지 등을 메뉴 매칭용으로 정규화)
const getParentPath = path => {
  const segments = path.split('/');
  return segments.length > 2 ? segments.slice(0, -1).join('/') : path;
};

// Main Component
const Header = ({ open, setOpen, isPinned, setIsPinned }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [, startTransition] = useTransition();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [adminSwitchOpen, setAdminSwitchOpen] = useState(false);
  const [isRegisterButtonHovered, setIsRegisterButtonHovered] = useState(false);
  const [showSidebarGuide, setShowSidebarGuide] = useState(() => {
    return !localStorage.getItem('sidebarGuideShown');
  });
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);
  const isAdminSwitched = !!localStorage.getItem(ORIGINAL_USER_KEY);
  const canSwitchAdmin = user?.userRole === 'ROLE_SUPERADMIN' || user?.userRole === 'ROLE_MANAGER' || isAdminSwitched;
  const [centerSearchKeyword, setCenterSearchKeyword] = useState('');
  const centerSearchRef = useRef(null);
  const [branchSearchKeyword, setBranchSearchKeyword] = useState('');
  const branchSearchRef = useRef(null);

  // SUPERADMIN: 본사 목록
  const { data: points = [] } = useQuery({
    queryKey: ['headBranches'],
    queryFn: async () => (await getHeadBranchs(user.token)) || [],
    enabled: user?.userRole === 'ROLE_SUPERADMIN' && !!user?.token,
  });

  // 선택된 센터에 속한 지점 목록
  const { data: branches = [] } = useQuery({
    queryKey: ['branches', user?.headBranch?.headBranchId],
    queryFn: async () => (await getHeadBranchId(user.token, user.headBranch.headBranchId)) || [],
    enabled: !!user?.headBranch?.headBranchId && !!user?.token,
  });

  // 센터/지점 목록을 이름 기준 가나다 순 정렬 (원본 배열 불변)
  const sortedPoints = useMemo(
    () =>
      points
        .filter(p => p.headBranchName !== '미지정')
        .sort((a, b) => (a.headBranchName || '').localeCompare(b.headBranchName || '', 'ko')),
    [points],
  );
  const filteredPoints = useMemo(
    () =>
      centerSearchKeyword
        ? sortedPoints.filter(p => matchKorean(p.headBranchName || '', centerSearchKeyword))
        : sortedPoints,
    [sortedPoints, centerSearchKeyword],
  );

  const groupedCenterItems = useMemo(() => {
    if (centerSearchKeyword) {
      return filteredPoints.map(point => (
        <BranchMenuItem key={point.headBranchId} value={point.headBranchId}>
          {point.headBranchName}
        </BranchMenuItem>
      ));
    }
    const items = [];
    let lastChosung = '';
    filteredPoints.forEach(point => {
      const chosung = getChosung((point.headBranchName || '').charAt(0));
      if (chosung !== lastChosung) {
        lastChosung = chosung;
        items.push(<ChosungDivider key={`g-${chosung}`}>{chosung}</ChosungDivider>);
      }
      items.push(
        <BranchMenuItem key={point.headBranchId} value={point.headBranchId}>
          {point.headBranchName}
        </BranchMenuItem>,
      );
    });
    return items;
  }, [filteredPoints, centerSearchKeyword]);

  const sortedBranches = useMemo(
    () =>
      branches
        .filter(branch => branch.branchName !== '미지정')
        .sort((a, b) => (a.branchName || '').localeCompare(b.branchName || '', 'ko')),
    [branches],
  );
  const filteredBranches = useMemo(
    () =>
      branchSearchKeyword
        ? sortedBranches.filter(branch => matchKorean(branch.branchName || '', branchSearchKeyword))
        : sortedBranches,
    [sortedBranches, branchSearchKeyword],
  );

  const resetSearch = useCallback(() => {
    setInputValue('');
    setSearchResults([]);
  }, []);

  // 페이지 이동 시 검색 입력값 초기화
  useEffect(() => {
    resetSearch();
  }, [location.pathname, resetSearch]);

  // 지점 자동 선택 로직 (기존 Studio 컴포넌트 역할을 Header가 흡수)
  // - 현재 선택된 user.branch 가 새 branches 목록 안에 존재하면 사용자가 직접 고른 값으로 보고 그대로 둔다.
  //   (그렇지 않으면 매번 user.branchId(로그인 시점 지점) 로 강제로 되돌려 수동 선택이 무력화됨)
  // - 선택값이 비어있거나 현재 헤드브랜치의 지점 목록에 더 이상 없으면 user.branchId 매칭 → 첫 지점 fallback.
  useEffect(() => {
    if (!user?.token || !user?.headBranch?.headBranchId || branches.length === 0) return;

    // 사용자가 이미 골라둔 지점이 현재 목록에 있으면 보정 안 함
    if (user?.branch?.branchId && branches.some(b => b.branchId === user.branch.branchId)) return;

    const matched = branches.find(b => b.branchId === user.branchId) ?? branches[0];
    if (matched) {
      dispatch(setUserData({ ...user, branch: matched }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, user?.headBranch?.headBranchId, user?.token, user?.branchId, user?.branch?.branchId]);

  // 지점을 바꾸면 복사해 둔 스케줄을 버린다 — 이전 지점의 강의실·강사·상품으로 새 지점에 붙는 것을 막는다.
  const clipboardBranchRef = useRef(user?.branch?.branchId);
  useEffect(() => {
    if (clipboardBranchRef.current !== user?.branch?.branchId) {
      clipboardBranchRef.current = user?.branch?.branchId;
      clearScheduleClipboard();
    }
  }, [user?.branch?.branchId]);

  // 센터(본사) 선택 핸들러
  const handleHeadBranchChange = e => {
    const selectedHeadBranchId = e.target.value;
    if (!selectedHeadBranchId || selectedHeadBranchId === 0) {
      dispatch(setUserData({ ...user, headBranch: null, branch: null }));
      return;
    }
    const selectedPoint = points.find(point => point.headBranchId === selectedHeadBranchId);
    if (selectedPoint) {
      // 센터 선택 시 지점은 초기화 (사용자가 직접 선택하도록)
      dispatch(
        setUserData({
          ...user,
          headBranch: selectedPoint,
          branch: null,
        }),
      );
    }
  };

  // 지점 선택 핸들러
  const handleBranchChange = e => {
    const selectedBranchId = e.target.value;
    if (!selectedBranchId || selectedBranchId === 0) {
      dispatch(setUserData({ ...user, branch: null }));
      return;
    }
    const selectedBranch = branches.find(branch => branch.branchId === selectedBranchId);
    if (selectedBranch) {
      dispatch(setUserData({ ...user, branch: selectedBranch }));
    }
  };

  // 계정 전환 시 이전 계정 데이터(React Query 캐시 / localStorage / AppContext / useState 등) 가
  // 새 로그인 화면에 잔존하지 않도록, 정리 후 풀 리로드한다. 401 만료 경로(utils/methods.js)와 동일 패턴.
  const logOut = () => {
    queryClient.clear();
    localStorage.removeItem('pila-user');
    localStorage.removeItem('user-menu');
    localStorage.removeItem(ORIGINAL_USER_KEY);
    localStorage.removeItem('pila-employee-cache');
    window.location.replace('/');
  };

  const restoreOriginalAdmin = () => {
    try {
      const original = JSON.parse(localStorage.getItem(ORIGINAL_USER_KEY));
      if (original?.user && original?.menu) {
        dispatch(setUserData(original.user));
        dispatch(setUserMenu(original.menu));
        localStorage.removeItem(ORIGINAL_USER_KEY);
        localStorage.removeItem('pila-employee-cache');
        queryClient.clear();
        window.location.replace('/member');
      }
    } catch {
      logOut();
    }
  };

  // 회원 검색 함수
  const getMembers = useCallback(
    async searchValue => {
      if (!searchValue || searchValue.trim().length === 0) {
        startTransition(() => {
          setSearchResults([]);
        });
        return;
      }

      try {
        const qstring = QueryString.stringify({
          searchInput: searchValue,
          headBranchId: user.headBranch?.headBranchId,
          branchId: user.branch?.branchId,
        });

        const [validData, temporaryData] = await Promise.all([
          getUsersList(user.token, qstring),
          getTemporaryUsersList(user.token, qstring),
        ]);

        const validMembers =
          validData?.content?.map(item => ({
            id: item.memberId,
            name: item.name + ' (' + item.phoneNumber + ')',
            memberName: item.name,
            phoneNumber: item.phoneNumber,
            userType: 'M',
          })) || [];

        const temporaryMembers =
          temporaryData?.content?.map(item => ({
            id: item.userId,
            name: item.name + ' (' + item.phoneNumber + ')',
            memberName: item.name,
            phoneNumber: item.phoneNumber,
            userType: 'T',
          })) || [];

        const transformArray = [...validMembers, ...temporaryMembers];

        startTransition(() => {
          setSearchResults(transformArray);
        });
      } catch (error) {
        console.error('회원 검색 중 오류 발생:', error);
      }
    },
    [user.headBranch?.headBranchId, user.branch?.branchId, user.token],
  );

  const debouncedGetMembers = useMemo(() => {
    let timeoutId;
    return searchValue => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => getMembers(searchValue), 300);
    };
  }, [getMembers]);

  const handleSection = section => {
    const targetPath = getParentPath(section);
    for (let menu of MenuList) {
      if (menu.path?.trim() === targetPath?.trim()) {
        return t(menu.name);
      }
      for (let subMenu of menu.subMenus || []) {
        if (subMenu.path?.trim() === targetPath?.trim()) {
          return t(subMenu.name);
        }
      }
    }
    return '';
  };

  const openMenu = Boolean(anchorEl);
  const handleUserMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRoute = route => {
    const targetPath = getParentPath(route);

    for (let menu of MenuList) {
      for (let subMenu of menu.subMenus || []) {
        if (subMenu.path?.trim() === targetPath?.trim()) {
          return (
            <>
              {t(menu.name)} <BreadcrumbIcon fontSize="small" /> {t(subMenu.name)}
            </>
          );
        }
      }
      if (menu.path?.trim() === targetPath?.trim()) {
        return t(menu.name);
      }
    }
    return (
      <>
        {t('OPERATORS')}
        <OperatorsText>{t('OPERATORS')}</OperatorsText>
      </>
    );
  };

  const dismissSidebarGuide = () => {
    setShowSidebarGuide(false);
    localStorage.setItem('sidebarGuideShown', 'true');
  };

  const handleMenuToggle = () => {
    if (showSidebarGuide) dismissSidebarGuide();
    if (open) {
      setIsPinned(false);
      setOpen(false);
    } else {
      setIsPinned(true);
      setOpen(true);
    }
  };

  const handleSearchChange = (e, v) => {
    if (v?.userType === 'M') {
      // Header에서 검색하여 이동했음을 표시 (새로고침과 구분하기 위함)
      sessionStorage.setItem('headerSearchNavigation', 'true');
      navigate(`/member?searchInput=${encodeURIComponent(v?.phoneNumber || '')}`);
    } else if (v?.userType === 'T') {
      sessionStorage.setItem('headerSearchNavigation', 'true');
      navigate(`/temporary?searchInput=${encodeURIComponent(v?.phoneNumber || '')}`);
    }
    startTransition(() => {
      resetSearch();
    });
  };

  // 자유 텍스트 검색 제출 (Enter / 검색 아이콘 클릭) — 검색결과 구성에 따라 라우팅 대상 분기.
  // 회원이 없고 잠재회원만 매칭된 경우만 잠재회원관리로 보내고, 그 외(빈 결과 포함)는 일반 회원관리로 보낸다.
  const submitFreeTextSearch = currentValue => {
    if (!currentValue) return;
    const hasMember = searchResults.some(r => r.userType === 'M');
    const hasTemporary = searchResults.some(r => r.userType === 'T');
    const targetPath = !hasMember && hasTemporary ? '/temporary' : '/member';
    sessionStorage.setItem('headerSearchNavigation', 'true');
    navigate(`${targetPath}?searchInput=${encodeURIComponent(currentValue)}`);
    resetSearch();
  };

  const canMemberRegister = useUserAuth('MEMBER_REGISTER');
  const isDisabled = !user?.branch?.branchId || !canMemberRegister;
  const showCenterPicker = user?.userRole === 'ROLE_SUPERADMIN';
  const showBranchPicker = showCenterPicker || user?.centerType === 'HEAD';

  return (
    <StyledAppBar position="fixed" open={open} color="inherit">
      <StyledToolbar>
        {/* 메뉴 토글 버튼 */}
        <Tooltip title={isPinned ? t('사이드바 고정 해제') : t('사이드바 고정')} placement="right" arrow>
          <MenuToggleButton isOpen={open} onClick={handleMenuToggle}>
            <MenuToggleIcon isPinned={isPinned} />
          </MenuToggleButton>
        </Tooltip>
        {showSidebarGuide && (
          <ClickAwayListener onClickAway={dismissSidebarGuide}>
            <SidebarGuide isOpen={open}>
              <SidebarGuideArrow />
              <SidebarGuideText>{t('이 버튼으로 사이드바를 고정할 수 있어요!')}</SidebarGuideText>
              <SidebarGuideClose onClick={dismissSidebarGuide}>✕</SidebarGuideClose>
            </SidebarGuide>
          </ClickAwayListener>
        )}

        {/* 왼쪽 영역 */}
        <LeftSection>
          <TitleWrapper>
            <PageTitle>{handleSection(location.pathname)}</PageTitle>
            <BreadcrumbWrapper>
              <BreadcrumbText component="div">{handleRoute(location.pathname)}</BreadcrumbText>
            </BreadcrumbWrapper>
          </TitleWrapper>

          {/* 센터/지점 선택 드롭다운 — SUPERADMIN은 둘 다, HEAD centerType 관리자는 지점만 */}
          {showBranchPicker && (
            <SelectGroup>
              {showCenterPicker && (
                <SelectWrapper>
                  <HighlightBorderWrapper showAnimation={!user?.headBranch?.headBranchId && isRegisterButtonHovered}>
                    <BranchFormControl size="small">
                      <BranchSelect
                        displayEmpty
                        value={user?.headBranch?.headBranchId || ''}
                        onChange={handleHeadBranchChange}
                        onClose={() => setCenterSearchKeyword('')}
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          autoFocus: false,
                          PaperProps: {
                            style: { maxHeight: '70vh' },
                          },
                          MenuListProps: {
                            style: { maxHeight: '70vh', overflow: 'auto' },
                            subheader: (
                              <CenterSearchInput
                                size="small"
                                autoFocus
                                placeholder="센터 검색"
                                value={centerSearchKeyword}
                                inputRef={centerSearchRef}
                                onChange={e => setCenterSearchKeyword(e.target.value)}
                                onKeyDown={e => e.stopPropagation()}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            ),
                          },
                        }}
                        renderValue={value => {
                          if (!value || value === 0) {
                            return <PlaceholderText>센터선택</PlaceholderText>;
                          }
                          const selectedPoint = points.find(point => point.headBranchId === value);
                          return selectedPoint?.headBranchName || '';
                        }}>
                        {groupedCenterItems}
                      </BranchSelect>
                    </BranchFormControl>
                  </HighlightBorderWrapper>
                </SelectWrapper>
              )}

              <SelectWrapper>
                <HighlightBorderWrapper showAnimation={isDisabled && isRegisterButtonHovered}>
                  <BranchFormControl size="small">
                    <BranchSelect
                      displayEmpty
                      value={user?.branch?.branchId || ''}
                      onChange={handleBranchChange}
                      onClose={() => setBranchSearchKeyword('')}
                      IconComponent={KeyboardArrowDownIcon}
                      disabled={!user?.headBranch?.headBranchId}
                      MenuProps={{
                        autoFocus: false,
                        PaperProps: { style: { maxHeight: '70vh' } },
                        MenuListProps: {
                          style: { maxHeight: '70vh', overflow: 'auto' },
                          subheader: (
                            <CenterSearchInput
                              size="small"
                              autoFocus
                              placeholder="지점 검색"
                              value={branchSearchKeyword}
                              inputRef={branchSearchRef}
                              onChange={e => setBranchSearchKeyword(e.target.value)}
                              onKeyDown={e => e.stopPropagation()}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          ),
                        },
                      }}
                      renderValue={value => {
                        if (!value || value === 0) {
                          return <PlaceholderText>지점선택</PlaceholderText>;
                        }
                        const selectedBranch = branches.find(branch => branch.branchId === value);
                        return selectedBranch?.branchName || '';
                      }}>
                      {filteredBranches.map(branch => (
                        <BranchMenuItem key={branch.branchId} value={branch.branchId}>
                          {branch.branchName}
                        </BranchMenuItem>
                      ))}
                    </BranchSelect>
                  </BranchFormControl>
                </HighlightBorderWrapper>
              </SelectWrapper>
            </SelectGroup>
          )}
        </LeftSection>

        {/* 오른쪽 영역 */}
        <RightSection>
          {/* 검색 필드 */}
          <SearchFieldWrapper>
            <StyledAutocomplete
              onChange={handleSearchChange}
              value={null}
              inputValue={inputValue}
              onInputChange={(e, newInputValue, reason) => {
                if (reason !== 'reset') {
                  setInputValue(newInputValue);
                  debouncedGetMembers(newInputValue);
                }
              }}
              disablePortal
              options={searchResults}
              getOptionLabel={option => option?.name || ''}
              noOptionsText="검색결과가 없습니다"
              renderOption={(props, option) => (
                // memberId 와 userId 는 서로 다른 ID 공간이라 값이 겹칠 수 있어 userType 을 key 에 포함한다.
                <li {...props} key={`${option.userType}-${option.id}`}>
                  <SearchOptionRow>
                    {/* 구분 뱃지는 이름 앞에 — 목록을 훑을 때 일반/잠재가 먼저 보이게 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                      <SearchTypeBadge isTemporary={option.userType === 'T'}>
                        {option.userType === 'T' ? '잠재' : '일반'}
                      </SearchTypeBadge>
                      <SearchOptionName>{option.memberName}</SearchOptionName>
                    </Box>
                    <SearchOptionPhone>{option.phoneNumber}</SearchOptionPhone>
                  </SearchOptionRow>
                </li>
              )}
              blurOnSelect={false}
              clearOnBlur={false}
              forcePopupIcon={false}
              renderInput={params => (
                <SearchTextField
                  placeholder="이름 및 연락처로 검색"
                  name="searchInput"
                  {...params}
                  label=""
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{ cursor: 'pointer' }}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => {
                          e.stopPropagation();
                          submitFreeTextSearch(inputValue?.trim());
                        }}>
                        <SearchPopupIcon />
                      </InputAdornment>
                    ),
                  }}
                  onKeyDown={e => {
                    // 한글 IME 조합 중이면 무시
                    if (e.nativeEvent.isComposing) return;

                    if (e.key === 'Enter') {
                      const currentValue = e.target.value?.trim();
                      if (currentValue) {
                        e.preventDefault();
                        e.stopPropagation();
                        submitFreeTextSearch(currentValue);
                      }
                    }
                  }}
                />
              )}
            />
          </SearchFieldWrapper>

          {/* 회원 등록 버튼 */}
          <Tooltip
            title={isDisabled ? (!canMemberRegister ? '권한이 없습니다' : '지점을 선택해주세요') : ''}
            placement="bottom"
            arrow
            componentsProps={tooltipStyles}>
            <span
              onMouseEnter={() => isDisabled && setIsRegisterButtonHovered(true)}
              onMouseLeave={() => setIsRegisterButtonHovered(false)}>
              <RegisterButton
                variant="outlined"
                disabled={isDisabled}
                isDisabled={isDisabled}
                startIcon={<UserIcon color={isDisabled ? '#a6a6a6' : '#4AB3BC'} />}
                onClick={() => setRegisterModalOpen(true)}>
                잠재회원 등록
              </RegisterButton>
            </span>
          </Tooltip>

          {/* 새 배포 감지 시 노출 */}
          <UpdateBadge />

          {/* 관리자 변경 */}
          {canSwitchAdmin && (
            <>
              <SwitchButton onClick={() => setAdminSwitchOpen(true)} endIcon={<img src="/images/icons/arrow-right-teal.png" alt="" style={{ width: 18, height: 12 }} />}>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.3 }}>
                  <span>{user?.userRole === 'ROLE_SUPERADMIN' ? '통합관리자' : `${user?.branch?.branchName || '관리자'}`}</span>
                  <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>
                    {user?.isDefaultAdmin === 'Y' ? '기본 관리자' : user?.name || ''}
                  </span>
                </span>
              </SwitchButton>
              <UserAvatar onClick={handleUserMenuOpen} style={{ marginLeft: '-16px' }}>
                <img src="/images/icons/profile-teal.png" alt="profile" style={{ width: 32, height: 32 }} />
              </UserAvatar>
            </>
          )}

          {/* 아바타 (관리자 변경 없을 때) */}
          {!canSwitchAdmin && (
            <UserAvatar onClick={handleUserMenuOpen}>
              <img src="/images/icons/profile-teal.png" alt="profile" style={{ width: 32, height: 32 }} />
            </UserAvatar>
          )}

          {/* 드롭다운 메뉴 */}
          <StyledMenu
            id="basic-menu"
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleUserMenuClose}
            PaperProps={menuPaperProps}
            MenuListProps={menuListProps}>
            <LogoutMenuItem component={Link} onClick={logOut} to="/">
              {t('로그아웃')}
            </LogoutMenuItem>
          </StyledMenu>
        </RightSection>
      </StyledToolbar>

      {/* 관리자 변경 모달 */}
      <AdminSwitchModal open={adminSwitchOpen} onClose={() => setAdminSwitchOpen(false)} />

      {/* 회원 등록 모달 */}
      <RegisterModal
        open={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        aria-labelledby="register-modal">
        <ModalContent>
          <TemporaryUserEdit onClose={() => setRegisterModalOpen(false)} isModal />
        </ModalContent>
      </RegisterModal>
    </StyledAppBar>
  );
};

export default Header;
